import { CAST, type Persona } from './cast.js';

/**
 * Table selection mechanics (spec §cast revision): each character appears at most ONCE per table,
 * empty seats fill with distinct characters, and problems are computed, not guessed — pinned by
 * tests, not only by the UI.
 */

/** Characters still choosable: everything not already taken by another seat. */
export function availablePersonas(taken: readonly string[]): Persona[] {
  return CAST.filter((p) => !taken.includes(p.id));
}

/** Fill every empty seat with the first distinct untaken character (deterministic, roster order). */
export function fillSelection(selection: Readonly<Record<string, string | undefined>>): Record<string, string> {
  const seats = Object.keys(selection);
  const taken = seats.map((s) => selection[s]).filter((v): v is string => Boolean(v));
  const filled: Record<string, string> = {};
  let pool = availablePersonas(taken);
  for (const seat of seats) {
    const chosen = selection[seat];
    if (chosen) {
      filled[seat] = chosen;
      continue;
    }
    const next = pool[0];
    if (!next) break; // more empty seats than characters — selectionProblems reports it
    filled[seat] = next.id;
    pool = pool.filter((p) => p.id !== next.id);
  }
  return filled;
}

/** Everything wrong with a selection, for the UI to show and tests to pin. */
export function selectionProblems(selection: Readonly<Record<string, string | undefined>>): string[] {
  const problems: string[] = [];
  const seen = new Map<string, string[]>();
  for (const [seat, id] of Object.entries(selection)) {
    if (!id) {
      problems.push(`${seat}: empty`);
      continue;
    }
    if (!CAST.some((p) => p.id === id)) problems.push(`${seat}: unknown persona ${id}`);
    seen.set(id, [...(seen.get(id) ?? []), seat]);
  }
  for (const [id, seats] of seen) {
    if (seats.length > 1) problems.push(`${id}: taken by ${seats.join(', ')}`);
  }
  return problems;
}
